import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Spinner } from "@material-tailwind/react";
import {
  ArrowDownTrayIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { portalApi } from "../../services/portalApi";

function escapeXml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function downloadSubscribersExcel(rows) {
  const cells = (vals) =>
    vals
      .map(
        (v) =>
          `<Cell><Data ss:Type="String">${escapeXml(v)}</Data></Cell>`
      )
      .join("");
  const header = cells(["Nombre", "Correo", "Estado", "Fecha suscripción", "Origen"]);
  const body = rows
    .map((r) => `<Row>${cells([r.name, r.email, r.status, r.subscribedAt, r.source])}</Row>`)
    .join("");
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="Suscriptores">
  <Table>
   <Row>${header}</Row>
   ${body}
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `conexion-siete-suscriptores-activos.xls`;
  a.click();
  URL.revokeObjectURL(url);
}

async function fetchAllActiveSubscribers() {
  const all = [];
  let offset = 0;
  const limit = 100;
  // ponytail: scan paginado; si crece mucho, endpoint /export dedicado
  for (;;) {
    const page = await portalApi.adminRevistaSubscribers({
      status: "active",
      limit,
      offset,
    });
    const batch = page.subscribers || [];
    all.push(...batch);
    const total = page.total ?? all.length;
    offset += batch.length;
    if (batch.length === 0 || offset >= total) break;
  }
  return all;
}

function IssueActionsMenu({ issue, onDeleted }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function onDelete() {
    setOpen(false);
    if (
      !window.confirm(
        `¿Eliminar «${issue.title || "este ejemplar"}»? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await portalApi.adminRevistaDeleteIssue(issue.issueId);
      toast.success("Ejemplar eliminado");
      onDeleted?.(issue.issueId);
    } catch (err) {
      toast.error(err?.message || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative inline-flex" ref={rootRef}>
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/30 text-brand-navy hover:bg-brand-lilac/40 disabled:opacity-50"
        aria-label="Acciones"
        aria-expanded={open}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open ? (
        <div className="absolute right-0 top-9 z-20 min-w-[9rem] rounded-lg border border-brand-lavender/25 bg-white py-1 shadow-lg">
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm text-brand-dark hover:bg-brand-beige"
            onClick={() => {
              setOpen(false);
              navigate(`/admin/revistas/${issue.issueId}`);
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            onClick={onDelete}
          >
            Eliminar
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminRevistasPanel() {
  const [issues, setIssues] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [iss, subs] = await Promise.all([
        portalApi.adminRevistaIssues(),
        portalApi.adminRevistaSubscribers({ status: "active", limit: 50, offset: 0 }),
      ]);
      setIssues(iss.issues || []);
      setSubscribers(subs.subscribers || []);
      setSubTotal(subs.total ?? 0);
    } catch (err) {
      setError(err?.message || "Error al cargar Conexión Siete");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onExportExcel() {
    setExporting(true);
    try {
      const rows = await fetchAllActiveSubscribers();
      if (!rows.length) {
        toast.error("No hay suscriptores activos");
        return;
      }
      downloadSubscribersExcel(rows);
      toast.success(`Excel: ${rows.length} suscriptores`);
    } catch (err) {
      toast.error(err?.message || "No se pudo exportar");
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-brand-gray">
        <Spinner className="h-5 w-5" /> Cargando Conexión Siete…
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600 text-sm py-6">{error}</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-brand-gray">
          Suscriptores activos: <strong className="text-brand-dark">{subTotal}</strong>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outlined"
            disabled={exporting || subTotal === 0}
            onClick={onExportExcel}
            className="normal-case border-brand-lavender text-brand-navy flex items-center gap-1.5"
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
            {exporting ? "Exportando…" : "Excel suscriptores"}
          </Button>
          <Link to="/admin/revistas/nuevo">
            <Button size="sm" className="bg-brand-lavender text-white normal-case">
              Nuevo ejemplar
            </Button>
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-brand-lavender/20 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-brand-lilac/30 text-left text-brand-navy">
            <tr>
              <th className="px-4 py-3 font-semibold">Título</th>
              <th className="px-4 py-3 font-semibold">Estado</th>
              <th className="px-4 py-3 font-semibold">PDF</th>
              <th className="px-4 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {issues.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-brand-gray text-center">
                  No hay ejemplares.{" "}
                  <Link to="/admin/revistas/nuevo" className="text-brand-lavender hover:underline">
                    Crea el primero
                  </Link>
                  .
                </td>
              </tr>
            ) : (
              issues.map((iss) => (
                <tr key={iss.issueId} className="border-t border-brand-lavender/15">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/revistas/${iss.issueId}`}
                      className="font-medium text-brand-dark hover:text-brand-lavender hover:underline"
                    >
                      {iss.title}
                    </Link>
                    {iss.publishedAt ? (
                      <div className="text-xs text-brand-gray mt-0.5">
                        Pub. {iss.publishedAt}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 capitalize">{iss.status || "—"}</td>
                  <td className="px-4 py-3">
                    {iss.s3Key ? (
                      iss.downloadUrl ? (
                        <Link
                          to={`/revistas/leer?u=${encodeURIComponent(iss.downloadUrl)}`}
                          className="text-brand-lavender hover:underline"
                        >
                          Leer
                        </Link>
                      ) : (
                        "Subido"
                      )
                    ) : (
                      <span className="text-brand-gray">Pendiente</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <IssueActionsMenu
                      issue={iss}
                      onDeleted={(id) => setIssues((prev) => prev.filter((x) => x.issueId !== id))}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-brand-dark">
            Últimos suscriptores (activo)
          </h3>
          <Button
            size="sm"
            variant="text"
            disabled={exporting || subTotal === 0}
            onClick={onExportExcel}
            className="normal-case text-brand-lavender flex items-center gap-1 px-2"
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
            Descargar Excel
          </Button>
        </div>
        <ul className="text-sm text-brand-gray space-y-1 max-h-48 overflow-y-auto">
          {subscribers.length === 0 ? (
            <li>Ninguno aún.</li>
          ) : (
            subscribers.map((s) => (
              <li key={s.email}>
                {s.name} · {s.email}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
